import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import MultiselectComponent from "../components/MultiselectComponent";
import {
  getOutputColumnsData,
  getMappings,
  saveAsMappings,
  getInputColumnsData,
  getEditStateData,
} from "../store/workspaceSlice";
import DropDownMenu from "../components/DropDownMenu";
import Button from "../components/Button";
import { rulesProperties, allowedRules, showAddedRulesToast } from "../helpers";
import { optionsForInputColumn } from "../helpers";
import CheckBox from "../components/CheckBox";
import { sortObjectByKeys } from "../utils";
const initialObjectProperties = {
  appliedRule: 8,
  useOutputOf: null,
  selectOptionFromInput: null,
  output: null,
  customColumnMappings: {},
  inputColumns: [],
  outputColumns: [],
  skipEmptyCells: false,
  skipZeroValues: false,
};

const CreateCustomColumn = () => {
  const dispatch = useDispatch();
  const [customColumn, setCustomColumn] = useState(initialObjectProperties);
  const [selectInputCols, setselectInputcols] = useState();
  const [selectOutputCols, setselectOutputCols] = useState();
  const outputColsData = useSelector(getOutputColumnsData);
  const inputColsData = useSelector(getInputColumnsData);
  const currentMaps = useSelector(getMappings);
  const [commonOptions, setCommonOptions] = useState([]);
  const state = useSelector(getEditStateData);
  const [isInLocalEditMode, setIsInLocalEditMode] = useState(false);

  useEffect(() => {
    if (state?.type === "CreateCustomColumn") {
      setselectInputcols(
        state.data.inputColumns.map((item) => ({
          label: item,
          value: item,
        }))
      );
      setselectOutputCols(
        state.data.outputColumns.map((item) => ({
          label: item,
          value: item,
        }))
      );
      setCustomColumn((prev) => ({ ...prev, ...state.data }));
      setIsInLocalEditMode(true);
    }
  }, [state?.data, state?.type, state?.data?._id]);

  useEffect(() => {
    if (isInLocalEditMode) {
      handlesetselectInputcols(state?.data?.inputColumns, true);
    }
  }, [isInLocalEditMode]);

  // for Date Column type week
  useEffect(() => {
    // Avoid running if useOutputOf is not set
    if (!customColumn.useOutputOf) return;

    const selectedMapping = currentMaps.find(
      (item) => String(item._id) === String(customColumn.useOutputOf)
    );

    if (
      selectedMapping?.dateColumnType === "week" &&
      !commonOptions.some((item) => item.value === "insertStartDate")
    ) {
      setCommonOptions((prev) => [
        ...prev,
        { label: "Insert Start Date", value: "insertStartDate" },
        { label: "Insert End Date", value: "insertEndDate" },
        {
          label: "Insert Start Date + Due Date",
          value: "insertStartDatePlusDueDate",
        },
        {
          label: "Insert End Date + Due Date",
          value: "insertEndDatePlusDueDate",
        },
      ]);
    }
  }, [customColumn.useOutputOf, currentMaps, commonOptions]);

  const handlesetselectInputcols = (selectedOptions, edit = false) => {
    setselectInputcols(selectedOptions);
    // Extract the `value` from selected options
    const selectedValues = selectedOptions.map((sel) => sel.value);

    // Filter the objects matching the selected values
    const selectedObjects = inputColsData.filter((obj) =>
      selectedValues.includes(obj.inColChr)
    );

    //  Define the specific keys to check
    const keysToCheck = [
      "inColDesc",
      "inColCharCode",
      "inColDueDate",
      "inColContactName",
      "inColExchangeRate",
      "inColAccountNo",
      "inColDcCode",
    ];

    // Find the keys that are not null or empty in all selected objects
    const validKeys = keysToCheck.filter((key) =>
      selectedObjects.every(
        (obj) => obj[key] !== null && obj[key] !== undefined && obj[key] !== ""
      )
    );
    // Convert valid keys into options
    const commonOpt = validKeys.map((key) => ({
      label: optionsForInputColumn[key], // Assuming optionsForInputColumn is an object mapping keys to labels
      value: key,
    }));

    // Set common options for the select dropdown
    setCommonOptions([
      { label: "Insert Date", value: "insertDate" },
      { label: "Insert Cell Data", value: "insertCellData" },
      { label: "Multiply by Exchange Rate", value: "exchangeRateMultiply" },
      { label: "Unique Record Identifier", value: "oid" },
      ...commonOpt,
    ]); // fine date default option
    // setCommonOptions(commonOpt); // fine date default option

    if (!edit) {
      setCustomColumn({
        ...customColumn,
        inputColumns: selectedValues, // Selected column keys
      });
    }
  };

  const handlesetselectOutputCols = (selectedOptions) => {
    setselectOutputCols(selectedOptions);
    setCustomColumn({
      ...customColumn,
      outputColumns: selectedOptions.map((sel) => sel.value),
    });
  };

  const handleOnChange = (e) => {
    setCustomColumn((prev) => {
      return {
        ...prev,
        [e.target.name]:
          e.target.type === "checkbox" ? e.target.checked : e.target.value,
      };
    });
  };

  const handleAddNewRule = () => {
    // Cleaning mappings data
    const cleanedMappings = Object.fromEntries(
      Object.entries(customColumn?.customColumnMappings).filter(([key]) =>
        customColumn?.inputColumns.includes(key)
      )
    );

    const updatedCustomColumnObject = {
      ...customColumn,
      customColumnMappings: sortObjectByKeys(cleanedMappings),
    };

    dispatch(
      saveAsMappings({
        ...updatedCustomColumnObject,
      })
    );

    showAddedRulesToast(
      customColumn.appliedRule,
      isInLocalEditMode ? "Updated" : "added"
    ); // Show toast notification
    reset();
  };

  const reset = () => {
    setCustomColumn(initialObjectProperties);
    setselectInputcols("");
    setselectOutputCols("");
    setIsInLocalEditMode(false);
  };

  return (
    <>
      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 p-2">
        <MultiselectComponent
          options={inputColsData.map((item) => ({
            label: item.inColChr,
            value: item.inColChr,
          }))}
          selectedOptions={selectInputCols}
          handleSetSelect={handlesetselectInputcols} // Handle the selection change
          label={"Select Input Columns"}
          required={true}
        />
        <MultiselectComponent
          options={outputColsData.map((item) => ({
            label: item.outColChr,
            value: item.outColChr,
          }))}
          selectedOptions={selectOutputCols}
          handleSetSelect={handlesetselectOutputCols} // Handle the selection change
          label={"Select Output Columns"}
          required={true}
        />{" "}
        <DropDownMenu
          label={"Use Output of previous Rule"}
          name="useOutputOf"
          value={customColumn.useOutputOf || ""}
          required={true}
          options={currentMaps
            .filter((item) => allowedRules.includes(item.appliedRule)) // Filter items based on allowed rules
            .map((item) => ({
              label: `${item._id} :  ${
                item.appliedRule
                  ? rulesProperties[item?.appliedRule].ruleName
                  : item.ruleName
              }`,
              value: parseInt(item._id),
            }))}
          onChange={handleOnChange}
        />
        {customColumn.outputColumns.length === 1 &&
          customColumn.inputColumns.length >= 1 && (
            <div className="col-span-2 flex justify-start items-end gap-x-8">
              <div className="min-h-10 flex">
                <CheckBox
                  name={"sameAsOutput"}
                  chkBoxText={"Same as output column"}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setCustomColumn((prevState) => ({
                        ...prevState,
                        customColumnMappings: Object.fromEntries(
                          Object.values(prevState.inputColumns).map(
                            (key) => [key, customColumn.outputColumns[0]] // 0 because will work if only one output column is selected
                          )
                        ),
                      }));
                    }
                  }}
                />
              </div>
            </div>
          )}
        {customColumn.outputColumns?.length > 0 &&
          customColumn.inputColumns?.length > 0 && (
            <div className="col-span-2 w-full p-2">
              <label className="font-semibold">
                Map switch columns to output column
              </label>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-2">
                {customColumn.inputColumns.map((column) => (
                  <DropDownMenu
                    key={column}
                    label={column}
                    name={column}
                    required={true}
                    value={customColumn.customColumnMappings[column] || ""}
                    options={[
                      { label: "Select column", value: "", disabled: true },
                      ...customColumn.outputColumns.map((col) => ({
                        label: col,
                        value: col,
                      })),
                    ]}
                    onChange={(e) => {
                      // Update the customColumnMappings state
                      setCustomColumn((prevState) => ({
                        ...prevState,
                        customColumnMappings: {
                          ...prevState.customColumnMappings,
                          [column]: e.target.value || "",
                        },
                      }));
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        <DropDownMenu
          label={"Select Options form input"}
          name="selectOptionFromInput"
          value={customColumn.selectOptionFromInput || ""}
          options={commonOptions}
          onChange={handleOnChange}
          required={true}
        />
        <div className="col-span-2 flex justify-start items-center gap-x-8">
          <div className="min-h-10 flex">
            <CheckBox
              name={"skipEmptyCells"}
              chkBoxText={"Skip Empty Cells"}
              checked={customColumn.skipEmptyCells}
              onChange={(e) => {
                handleOnChange(e);
              }}
            />
          </div>
          <div className="min-h-10 flex">
            <CheckBox
              name={"skipZeroValues"}
              chkBoxText={"Skip Zero Values"}
              checked={customColumn.skipZeroValues}
              onChange={(e) => {
                handleOnChange(e);
              }}
            />
          </div>
        </div>
        <div className="mt-4 col-span-2 flex gap-2">
          <Button
            btnText={`${isInLocalEditMode ? "Update" : "Save"}`}
            handleOnClick={handleAddNewRule}
            disabled={
              customColumn.inputColumns.length === 0 ||
              customColumn.outputColumns.length === 0 ||
              // !customColumn.useOutputOf ||  optional in shopee
              !customColumn.selectOptionFromInput
            }
          />
          <Button
            btnType="cancel"
            btnText={"Reset"}
            handleOnClick={() => {
              reset();
            }}
          />
        </div>
      </div>
    </>
  );
};

export default CreateCustomColumn;
